/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Component} from 'vue-property-decorator';
import IssueTitleInfo from '@/components/IssueTitleInfo/index.vue';
import {Action, Getter, State} from 'vuex-class';
import {
    DetailsStageInterface,
    FormStateInterface,
    HardwareInterface, PCHardwareInterface,
} from '@/store/modules/form/types';
import {GameInterface, GamePlatformInterface} from '@/store/modules/games/types';
import MediaDragDrop from '@/components/MediaDragDrop/index.vue';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import {mixins} from 'vue-class-component';
import Loading from '@/components/Loading/index.vue';
import Scroller from '@/mixins/Scroller';
import {api, auth} from '@/services/http';
import {AxiosResponse} from 'axios';
import countries from '@/assets/countries.json';

/**
 * Report Stage 3.Details component.
 *
 * @author nchapman@frontier.co.uk, jgeorgiadou@frontier.co.uk
 *
 */
@Component({
    name: 'c-report-stage-details',
    components: {
        'c-loading': Loading,
        'c-issue-title-info': IssueTitleInfo,
        'c-file-drag-drop': MediaDragDrop,
    },
})
export default class ReportStageDetails extends mixins(Scroller) {
    // Forms models
    @State('formStateModule') public formState!: FormStateInterface;
    @Action('cancelReportIssue', {namespace: 'formStateModule'}) public cancelReportIssue!: () => void;
    @Action('updateStage', {namespace: 'formStateModule'}) public updateStage!: (stage: number) => void;

    // Games
    @Action('fetchGames', {namespace: 'gamesStateModule'}) public fetchGames!: () => Promise<void>;
    @Getter('getGames', {namespace: 'gamesStateModule'}) public getGames!: GameInterface[];
    public game: GameInterface| null = null;
    public platform: GamePlatformInterface | null = null;

    ////////////////////////////////////
    // Form fields
    // The Problem
    public description: string = '';
    public reproductionSteps: string = '';
    public timesSeen: any = null;
    public timesSeenError: boolean = false;
    public loadedViaError: boolean = false;
    public readonly MAX_FILE_SIZE_IMAGE: number = 104857600;
    public readonly MAX_FILE_COUNT: number = 4;
    public images: File[] = [];
    // Your hardware
    public platformType: string = '';
    public os: string = '';
    public graphicsCard: string = '';
    public loadedVia: string = '';
    public systemMemory: string = '';
    public processor: string = '';
    public directX: string = '';
    public region: string = '';
    public internetProvider: string = '';
    public internetSpeed: string = '';
    public additionalTechnicalInfo: string = '';
    // Your Subscription
    public subscription: boolean = false;

    public state: LoadingStateInterface = new LoadingState(false, false);

    public hardwareLocalStorage: any = '';

    public totalSteps: number = 1;

    public reproductionStepsArray: any[] = [''];

    public reproductionStep1: string = '';

    public reproductionStep2: string = '';

    public reproductionStep3: string = '';

    public hardware: any = '';

    public regions: any[] = [];

    // Forms
    @Action('updateDetailsStage', {namespace: 'formStateModule'})
    private updateDetailsStage!: (details: DetailsStageInterface) => void;

    /**
     * Mounted life-cycle event.
     *
     * Initialises the loader.
     */
    public async mounted(): Promise<void> {
        // add ?step=3 to the url so browser back button can go back to step 2
        history.pushState({page: 3}, 'Details', '?step=3');
        window.onpopstate = (
            (event: PopStateEvent): void => {
                const nextPage: number = event.state.page;

                if (nextPage === this.formState.stage - 1) {
                    this.previous();
                }
            });

        // Api endpoint to get all countries, has Mozilla Public License.
        // Source: (https://restcountries.eu/#api-endpoints)
        this.regions = countries;

        try {
            this.state.start();
            await this.fetchGames();

            // if user rejects Preferences cookies, don't retrieve it from the localStorage, vice versa
            // @ts-ignore
            if (Cookiebot.consent.preferences) {
                this.hardwareLocalStorage = localStorage.getItem('hardware');
                this.hardware = JSON.parse(this.hardwareLocalStorage);
            }

            // set hardware to be the same as the form details, if it has not been set before
            if (this.hardware === null) {
                this.hardware = this.formState.detailsStage.hardware;
            }

            this.game = this.getGames.find((game: GameInterface): boolean => {
                return this.formState.reportStage.game === game.id;
            }) as GameInterface;

            this.platform = this.game.platforms.find((platform: GamePlatformInterface): boolean => {
                return platform.id === this.formState.reportStage.platform;
            }) as GamePlatformInterface;

            // Instantiating/Updating from state the Problem fields
            this.description = this.formState.detailsStage.problem.problemDescription || '';
            this.reproductionSteps = this.formState.detailsStage.problem.reproductionSteps || '';
            this.timesSeen = this.formState.detailsStage.problem.timesSeen;

            this.region = this.hardware.region || this.formState.detailsStage.hardware.region || '';
            this.internetProvider = this.hardware.internetProvider ||
                this.formState.detailsStage.hardware.internetProvider || '';
            this.internetSpeed = this.hardware.internetSpeed ||
                this.formState.detailsStage.hardware.internetSpeed || '';
            this.additionalTechnicalInfo = this.hardware.additionalTechnicalInfo ||
                this.formState.detailsStage.hardware.additionalTechnicalInfo || '';

            if (this.formState.detailsStage.problem.reproductionSteps) {
                const storedReproductionSteps: string[] = this.formState.detailsStage.problem.reproductionSteps.
                substring(1, this.formState.detailsStage.problem.reproductionSteps.length - 1).split('<p>');

                let i: number = 0;
                for (const eachStoredStep of storedReproductionSteps) {
                    this.reproductionStepsArray[i] = this.stripPTagsFromStep(eachStoredStep);
                    i++;
                }

                this.totalSteps = this.reproductionStepsArray.length;
                // populate out the Vue bound field properties with the appropriate values, so they appear in the form
                this.reproductionStep1 = this.reproductionStepsArray[0];
                this.reproductionStep2 = this.reproductionStepsArray[1];
                this.reproductionStep3 = this.reproductionStepsArray[2];
            }

            // Instantiating/Updating from state the Hardware fields
            if (this.platform && (this.platform.prefix === 'pc' || this.platform.prefix === 'mac')) {
                this.platformType = (this.formState.detailsStage.hardware as PCHardwareInterface).type || '';
                this.os = (this.formState.detailsStage.hardware as PCHardwareInterface).os || '';
                this.graphicsCard = (this.formState.detailsStage.hardware as PCHardwareInterface).graphicsCard || '';
                this.loadedVia = (this.formState.detailsStage.hardware as PCHardwareInterface).loadedVia || '';
                this.systemMemory = (this.formState.detailsStage.hardware as PCHardwareInterface).systemMemory || '';
                this.processor = (this.formState.detailsStage.hardware as PCHardwareInterface).processor || '';
                this.directX = (this.formState.detailsStage.hardware as PCHardwareInterface).directVersion || '';
            }

            if (this.platform.prefix === 'ps') {
                this.platformType = this.hardware.playstationFour_type || (this.formState.detailsStage.hardware as
                    PCHardwareInterface).type || '';
            }

            if (this.platform.prefix === 'xbox') {
                this.platformType = this.hardware.xboxOne_type || (this.formState.detailsStage.hardware as
                    PCHardwareInterface).type || '';
            }

            this.subscription = this.formState.detailsStage.subscription || false;
            this.state.complete();
        } catch (e) {
            this.state.failed(e.message);
        } finally {
            this.scrollTop();
        }
    }

    /**
     * Update the state and ensure select fields have valid options set
     *
     * @return {Promise<void>}
     */
    public async updateFormValues(): Promise<void> {
        if (this.game) {
            let hardware: HardwareInterface = {
                region: this.region,
                internetProvider: this.internetProvider,
                internetSpeed: this.internetSpeed,
                additionalTechnicalInfo: this.additionalTechnicalInfo,
                mediaFiles: this.images,
                type: this.platformType,
                playstationFour_type: this.hardware.playstationFour_type || '',
                xboxOne_type: this.hardware.xboxOne_type || '',
            };

            if (this.platform && (this.platform.prefix === 'pc' || this.platform.prefix === 'mac')) {
                hardware = Object.assign(hardware, {
                    os: this.os,
                    loadedVia: this.loadedVia || 'steam_launcher',
                    graphicsCard: this.graphicsCard,
                    systemMemory: this.systemMemory,
                    processor: this.processor,
                    directVersion: this.directX,
                }) as PCHardwareInterface;
            }

            this.updateDetailsStage({
                problem: {
                    problemDescription: this.description,
                    reproductionSteps: this.serializeReproductionSteps(),
                    timesSeen: this.timesSeen,
                },
                hardware,
                subscription: this.subscription,
            });

            // for each platform, save the specific type
            if (this.platform !== null && this.platform.prefix === 'xbox') {
                hardware.xboxOne_type = this.platformType;
            }
            if (this.platform !== null && this.platform.prefix === 'ps') {
                hardware.playstationFour_type = this.platformType;
            }

            // if user rejects preferences cookies, don't save it to the localStorage, vice versa
            // ignore ts error since it's not really an error, it's caused by ts can't recognize Cookiebot
            // @ts-ignore
            if (Cookiebot.consent.preferences) {
                localStorage.setItem('hardware', JSON.stringify(hardware));
            }
        }
    }

    /**
     *  Progress to the next step if form values are valid.
     *
     */
    public previous(): void {
        this.updateStage(this.formState.stage - 1);
    }

    /**
     *  Progress to the next step if form values are valid.
     *
     */
    public next(): void {
        this.checkFrequencyError();
        this.checkLoadedViaError();
        this.$validator.validateAll().then((result: boolean) => {
            if (result) {
                this.updateStage(this.formState.stage + 1);
            }
        });
    }

    /**
     * Check if the user has selected the frequency.
     */
    public checkFrequencyError(): void {
        if (typeof this.timesSeen === 'undefined' || this.timesSeen == null) {
            this.timesSeenError = true;
            return;
        }

        if (typeof this.timesSeen !== 'undefined' && this.timesSeen !== null) {
            this.timesSeenError = false;
            return;
        }
    }

    public checkLoadedViaError(): void {
        if (typeof this.loadedVia === 'undefined' || this.loadedVia === '') {
            this.loadedViaError = true;
            return;
        }

        if (typeof this.loadedVia !== 'undefined' && this.loadedVia !== '') {
            this.loadedViaError = false;
            return;
        }
    }

    private serializeReproductionSteps(): string {
        if (this.reproductionStep1 !== '') {
            this.reproductionStepsArray[0] = this.reproductionStep1;
        }

        if (this.totalSteps > 1 && this.reproductionStep2 !== '') {
            this.reproductionStepsArray[1] = this.reproductionStep2;
        }

        if (this.totalSteps > 2 && this.reproductionStep3 !== '') {
            this.reproductionStepsArray[2] = this.reproductionStep3;
        }

        let serializedSteps: string = '';
        if (this.reproductionStepsArray.length >= 1) {
            for (const step of this.reproductionStepsArray) {
                if (step === '') {
                    continue;
                }

                serializedSteps = serializedSteps + '<p>' + step + '</p>';
            }
        }

        return serializedSteps;
    }

    private stripPTagsFromStep(step: string): string {
        let wipStep: string = step.replace('<p', '');
        wipStep = wipStep.replace( '</p>', '');
        wipStep = wipStep.replace( '</p', '') ;
        wipStep = wipStep.replace( 'p>', '');

        return wipStep;
    }
}
